// Deck 1's own corridor light strips as wayfinding and the alert (Grey, 2026-10-23: "using the visual indicators in corridors for which
// direction you are facing ... Do not place any external lights from the system that we already have established"). The ship's one
// strip mesh (the baseboard strip along the foot of the walls, and the header strip along their top) is drawn by its own material:
//  - where a corridor runs bow to stern, both strips carry a gentle pulse that runs toward the bow
//  - both strips take the ship's condition: white at normal, green while Condition Green is being called (egress and departure),
//    fading back to white once it's confirmed, and the alert's own colour at yellow, red and blue alert (the status bar's palette)
// (The red and green sidelights in the athwartship corridors are gone again: Grey, 2026-10-23, "get rid of the red green lights".)
// All in the strips' shader: nothing added to the ship, nothing redrawn from the CPU (a phone pays nothing for it). The bow is +x in the
// scene.
import { Color3, MaterialPluginBase, PBRMaterial, StandardMaterial, Texture, type AbstractMesh, type Light, type Material, type Scene, type UniformBuffer } from "@babylonjs/core";
import { ShaderLanguage } from "@babylonjs/core/Materials/shaderLanguage.js";

const STRIP = /^D[12]_Corr_Strip_Glow(_primitive\d+)?$/;   // (Deck 2's too: built from the floor plan, 2026-10-26)
const WHITE = new Color3(0.86, 0.94, 1);
// the ship's conditions, as the status bar colours them (client/screens.ts setAlert); Condition Green (shared/green.ts) its own
export const STRIP_COLOURS: Record<string, Color3> = { normal: WHITE, green: new Color3(0.32, 0.98, 0.52), yellow: new Color3(1, 0.72, 0.1), red: new Color3(1, 0.12, 0.08), blue: new Color3(0.2, 0.48, 1) };
const WAVE = 5, SPEED = 1.6, LOW = 0.45;   // the pulse: a crest every 5 m, 1.6 m/s toward the bow, down to 45% between
const FADE = 60;                           // seconds: Condition Green confirmed (or over), back to white over a minute (Grey, 2026-10-24)

class StripPlugin extends MaterialPluginBase {
  time = 0; tint = WHITE.clone();
  constructor(material: Material) { super(material, "CorridorStrip", 200, { CORRIDORSTRIP: false }); this.registerForExtraEvents = true; this._enable(true); }   // (extra events: the bind on every draw)
  override getClassName() { return "CorridorStripPlugin"; }
  override isCompatible(lang: ShaderLanguage) { return lang === ShaderLanguage.GLSL || lang === ShaderLanguage.WGSL; }
  override prepareDefines(defines: Record<string, unknown>) { defines.CORRIDORSTRIP = true; }
  override getUniforms(lang?: ShaderLanguage) {
    return { ubo: [{ name: "stripTime", size: 1, type: "float" }, { name: "stripTint", size: 3, type: "vec3" }],
      fragment: lang === ShaderLanguage.WGSL ? "" : "#ifdef CORRIDORSTRIP\nuniform float stripTime; uniform vec3 stripTint;\n#endif" };
  }
  // (every draw, not only when Babylon thinks the material changed: the pulse's clock moves on every frame)
  override bindForSubMesh(ubo: UniformBuffer) { this.hardBindForSubMesh(ubo); }
  override hardBindForSubMesh(ubo: UniformBuffer) { ubo.updateFloat("stripTime", this.time); ubo.updateColor3("stripTint", this.tint); }
  override getCustomCode(shaderType: string, lang?: ShaderLanguage): Record<string, string> | null {
    if (shaderType !== "fragment") return null;
    const k = (2 * Math.PI / WAVE).toFixed(5), w = (2 * Math.PI * SPEED / WAVE).toFixed(5), lo = LOW.toFixed(3);
    if (lang === ShaderLanguage.WGSL) return { CUSTOM_FRAGMENT_BEFORE_FRAGCOLOR: `
#ifdef CORRIDORSTRIP
{ let sp = fragmentInputs.vPositionW; let sn = normalW;
  var c = uniforms.stripTint;
  if (abs(sn.z) > 0.8) { let s = 0.5 + 0.5 * sin(${k} * sp.x - ${w} * uniforms.stripTime); c = c * (${lo} + (1.0 - ${lo}) * s * s); }
  color = vec4f(c, color.a); }
#endif` };
    return { CUSTOM_FRAGMENT_BEFORE_FRAGCOLOR: `
#ifdef CORRIDORSTRIP
{ vec3 sp = vPositionW; vec3 sn = normalW;
  vec3 c = stripTint;
  if (abs(sn.z) > 0.8) { float s = 0.5 + 0.5 * sin(${k} * sp.x - ${w} * stripTime); c *= ${lo} + (1.0 - ${lo}) * s * s; }
  color = vec4(c, color.a); }
#endif` };
  }
}
export class CorridorLights {
  private plugins: StripPlugin[] = [];
  constructor(scene: Scene, meshes: AbstractMesh[]) {
    for (const m of meshes) {
      if (!STRIP.test(m.name)) continue;
      const mat = new StandardMaterial("d1_strip", scene); mat.diffuseColor = Color3.Black(); mat.specularColor = Color3.Black(); mat.emissiveColor = WHITE.clone(); mat.disableLighting = true;
      this.plugins.push(new StripPlugin(mat)); m.material = mat;
    }
    console.log(`corridor lights: ${this.plugins.length} strip mesh(es)`);
  }
  // the ship's condition: "normal", "green" (Condition Green being called), "yellow", "red", "blue". An alert changes the strips at
  // once; Condition Green confirmed (or over) fades them back to white
  private cond = "normal"; private tint = WHITE.clone(); private from = WHITE.clone(); private fadeAt = 0;
  private lamp = new Color3(1, 1, 1); private out = WHITE.clone();
  get tintNow() { return this.out; }   // (the strips' colour now, fades and all, under the ship's lighting: the baked light on the corridors takes it too)
  // the ship's lighting (Grey, 2026-10-24: "ship's lighting doesnt affect the corridoors and it should"): its warmth, and the emergency
  // red with no power to the lighting, laid over the condition's colour (client/roomlight.ts corridor)
  setLamp(c: [number, number, number]) { this.lamp.set(c[0], c[1], c[2]); }
  setCondition(c: string) {
    if (c === this.cond) return;
    if (this.cond === "green" && c === "normal") { this.from.copyFrom(this.tint); this.fadeAt = performance.now(); } else { this.fadeAt = 0; this.tint.copyFrom(STRIP_COLOURS[c] ?? WHITE); }
    this.cond = c;
  }
  tick() {
    const now = performance.now(), t = (now / 1000) % (WAVE / SPEED);   // (one period of the pulse round: small numbers keep a phone's GPU precise)
    if (this.fadeAt) { const f = Math.min(1, (now - this.fadeAt) / (FADE * 1000)), e = f * f * (3 - 2 * f); Color3.LerpToRef(this.from, STRIP_COLOURS[this.cond] ?? WHITE, e, this.tint); if (f >= 1) this.fadeAt = 0; }
    this.tint.multiplyToRef(this.lamp, this.out);
    for (const p of this.plugins) { p.time = t; p.tint.copyFrom(this.out); }
  }
  state() { return { cond: this.cond, tint: [+this.tint.r.toFixed(3), +this.tint.g.toFixed(3), +this.tint.b.toFixed(3)], out: [+this.out.r.toFixed(3), +this.out.g.toFixed(3), +this.out.b.toFixed(3)], fading: !!this.fadeAt }; }   // (tests)
}

// ---------- the strips' light on the corridors, baked (ships/corridor_lightmap.py; Grey, 2026-10-23: "lets go with that corridor
// lighting", after the Cycles target in renders/corridor_proof/) ----------
// Cycles worked out what the strips (white) throw onto every corridor wall, floor and ceiling, falloff and bounce and all, into one
// lightmap on the meshes' second UVs. Here it's real diffuse light: the baked light times the surface's own colour, times the strips'
// condition colour (light adds, so a red strip's light is this light in red), added to the rest of the lighting, which is turned well
// down on those surfaces (the strips are the corridor's lamps). Medium and High quality only (a 4096 square map: not for a phone).
const LM_URL = "assets/env/corridor_lightmap.jpg", LM_VER = 10;
const LM_GAIN = 1.0, LM_AMBIENT = 0.22;   // the bake's light as Cycles has it; what's left of the sky light and the probe on the corridors
class BakedPlugin extends MaterialPluginBase {
  tint = WHITE.clone();
  constructor(material: Material) { super(material, "CorridorBaked", 210, { CORRBAKED: false }); this.registerForExtraEvents = true; this._enable(true); }
  override getClassName() { return "CorridorBakedPlugin"; }
  override isCompatible(lang: ShaderLanguage) { return lang === ShaderLanguage.GLSL || lang === ShaderLanguage.WGSL; }
  override prepareDefines(defines: Record<string, unknown>) { defines.CORRBAKED = true; }
  override getUniforms(lang?: ShaderLanguage) {
    return { ubo: [{ name: "corrTint", size: 3, type: "vec3" }], fragment: lang === ShaderLanguage.WGSL ? "" : "#ifdef CORRBAKED\nuniform vec3 corrTint;\n#endif" };
  }
  override bindForSubMesh(ubo: UniformBuffer) { this.hardBindForSubMesh(ubo); }
  override hardBindForSubMesh(ubo: UniformBuffer) { ubo.updateColor3("corrTint", this.tint); }
  // (Babylon's own lightmap path samples it on the second UVs; here it becomes diffuse light, and Babylon's plain add is cancelled)
  override getCustomCode(shaderType: string, lang?: ShaderLanguage): Record<string, string> | null {
    if (shaderType !== "fragment") return null;
    if (lang === ShaderLanguage.WGSL) return { CUSTOM_FRAGMENT_BEFORE_FINALCOLORCOMPOSITION: `
#if defined(CORRBAKED) && defined(LIGHTMAP)
finalDiffuse = finalDiffuse + surfaceAlbedo * lightmapColor.rgb * uniforms.corrTint;
lightmapColor = vec4f(0.0);
#endif` };
    return { CUSTOM_FRAGMENT_BEFORE_FINALCOLORCOMPOSITION: `
#if defined(CORRBAKED) && defined(LIGHTMAP)
finalDiffuse += surfaceAlbedo * lightmapColor.rgb * corrTint;
lightmapColor = vec4(0.0);
#endif` };
  }
}
interface BakeMan { scale: number; objects: string[]; materials: string[] }
export class CorridorBake {
  private man: BakeMan | null = null; private tex: Texture | null = null; private on = false; private loading = false;
  private swaps: { m: AbstractMesh; orig: Material; lit: PBRMaterial }[] = []; private plugins: BakedPlugin[] = [];
  constructor(private scene: Scene, private meshes: AbstractMesh[], private lamps: Light[]) {}
  private async load() {
    this.loading = true;
    try { this.man = await (await fetch(`assets/env/corridor_lightmap.json?v=${LM_VER}`)).json() as BakeMan; } catch (e) { console.warn("corridor lightmap: no manifest", e); return; }
    const t = new Texture(`${LM_URL}?v=${LM_VER}`, this.scene, false, false); t.coordinatesIndex = 1; t.gammaSpace = true; t.level = this.man.scale * LM_GAIN; this.tex = t;
    const objs = new Set(this.man.objects), mats = new Set(this.man.materials), lit = new Map<Material, PBRMaterial>();
    for (const m of this.meshes) {
      const base = m.name.replace(/_primitive\d+$/, ""), mt = m.material;
      if (!objs.has(base) || !mt || !mats.has(mt.name) || !(mt instanceof PBRMaterial) || !m.isVerticesDataPresent("uv2")) continue;
      let l = lit.get(mt);
      if (!l) { l = mt.clone(mt.name + "_corridorLit") as PBRMaterial; l.lightmapTexture = t; l.useLightmapAsShadowmap = false; l.environmentIntensity = LM_AMBIENT; this.plugins.push(new BakedPlugin(l)); lit.set(mt, l); }
      this.swaps.push({ m, orig: mt, lit: l });
    }
    console.log(`corridor lightmap: ${this.swaps.length} corridor meshes, ${lit.size} materials`);
    this.apply();
  }
  private apply() {
    for (const s of this.swaps) s.m.material = this.on ? s.lit : s.orig;
    const ms = this.swaps.map((s) => s.m);
    for (const l of this.lamps) { const ex = l.excludedMeshes.filter((m) => !ms.includes(m)); l.excludedMeshes = this.on ? [...ex, ...ms] : ex; }   // the sky light and the sun don't reach in
  }
  setOn(on: boolean) { if (on === this.on && (this.man || !on)) return; this.on = on; if (on && !this.man && !this.loading) void this.load(); else if (this.man) this.apply(); }
  setTint(c: Color3) { for (const p of this.plugins) p.tint.copyFrom(c); }
  state() { return { on: this.on, meshes: this.swaps.length, ready: !!this.tex?.isReady() }; }
}
